import IconComponent from "@mdi/react";
import { useCallback, useEffect, useLayoutEffect, useRef } from "react";
import { OutPortal } from "react-reverse-portal";

import { mdiClose, mdiFullscreen, mdiFullscreenExit, mdiWindowRestore } from "@mdi/js";
import {
  PANEL_GUTTER,
  PANEL_MIN_HEIGHT,
  PANEL_VERTICAL_MARGIN,
  PANEL_WIDTH,
  WELL_CARD_PANEL_TABS,
  clampPanelPosition,
  selectWellCardCustomLineActive,
  selectWellCardPanelHeight,
  selectWellCardPanelMaximized,
  selectWellCardPanelMinimized,
  selectWellCardPanelOpen,
  selectWellCardPanelPosition,
  selectWellCardPanelTab,
  selectWellCardPanelWidth,
  selectWellCardScreenshotNode,
  selectWellCardTargetUwi,
  useWellCardPanelStore
} from "stores/wellCardPanel";
import type { PanelPosition } from "stores/wellCardPanel";
import { effectivePanelWidth } from "stores/wellCardPanel/wellCardPanelStore";
import styled from "styled-components";

import { Uwi } from "models";

import { Tooltip } from "components/base";
import useSelectedWellFromStore from "components/data-analyzer/hooks/useSelectedWellFromStore";

import { PanelChart } from "./PanelChart";
import {
  BodySkeleton,
  ChartSkeleton,
  HeaderSkeleton,
  NoProductionState,
  NoSelectionState,
  PanelErrorState
} from "./PanelStates";
import { StateFilingsLink } from "./StateFilingsLink";
import { WellCardPanelHeader } from "./WellCardPanelHeader";
import { useChromeInsetLeft } from "./hooks/useChromeInsetLeft";
import { useDeriveXdaLineForWell } from "./hooks/useDeriveXdaLineForWell";
import { useLineHalfLengthMeters } from "./hooks/useLineHalfLengthMeters";
import { usePanelDrag } from "./hooks/usePanelDrag";
import { usePanelResize } from "./hooks/usePanelResize";
import { useProductionArrayData } from "./hooks/useProductionArrayData";
import { useWellCardPanelFields } from "./hooks/useWellCardPanelFields";
import { TEAL } from "./styles";
import { DetailsTab } from "./tabs/DetailsTab";
import { GunbarrelTab } from "./tabs/GunbarrelTab";
import { ProductionTab } from "./tabs/ProductionTab";
import { SummaryTab } from "./tabs/SummaryTab";

export interface WellCardPanelProps {
  dashboardId: string;
}

/**
 * Mounted on every dashboard, open or shut. It holds the open flag and nothing
 * else: every fetch, subscription and block lives in `WellCardPanelBody`, which
 * is mounted only while the card is open. A closed card that called the data
 * hooks would keep two queries and the chart's hover subscriptions alive on
 * dashboards that never show it.
 */
export function WellCardPanel({ dashboardId }: WellCardPanelProps): JSX.Element {
  const hydrate = useWellCardPanelStore((s) => s.hydrate);
  const open = useWellCardPanelStore(selectWellCardPanelOpen);
  useEffect(() => {
    hydrate(dashboardId);
  }, [dashboardId, hydrate]);

  if (!open) return null;

  return <WellCardPanelBody />;
}

/**
 * Fixed-position overlay beside the dashboard grid. Only the active tab's
 * blocks render, but the chart sits above the tab switch: Summary and
 * Production share one instance, and the tabs that do not show it hide it
 * rather than take it down.
 */
function WellCardPanelBody(): JSX.Element {
  const maximized = useWellCardPanelStore(selectWellCardPanelMaximized);
  const minimized = useWellCardPanelStore(selectWellCardPanelMinimized);
  const position = useWellCardPanelStore(selectWellCardPanelPosition);
  const panelWidth = useWellCardPanelStore(selectWellCardPanelWidth);
  const panelHeight = useWellCardPanelStore(selectWellCardPanelHeight);
  const activeTab = useWellCardPanelStore(selectWellCardPanelTab);
  const targetUwi = useWellCardPanelStore(selectWellCardTargetUwi);
  const halfLengthMeters = useLineHalfLengthMeters();
  const customLineActive = useWellCardPanelStore(selectWellCardCustomLineActive);
  const screenshotNode = useWellCardPanelStore(selectWellCardScreenshotNode);
  const setOpen = useWellCardPanelStore((s) => s.setOpen);
  const setMinimized = useWellCardPanelStore((s) => s.setMinimized);
  const undock = useWellCardPanelStore((s) => s.undock);
  const setPosition = useWellCardPanelStore((s) => s.setPosition);
  const setPanelWidth = useWellCardPanelStore((s) => s.setPanelWidth);
  const setPanelHeight = useWellCardPanelStore((s) => s.setPanelHeight);
  const setActiveTab = useWellCardPanelStore((s) => s.setActiveTab);
  const setTargetUwi = useWellCardPanelStore((s) => s.setTargetUwi);
  const toggleMaximized = useWellCardPanelStore((s) => s.toggleMaximized);

  // Maximized, the card fills the space to the right of the left rail, which
  // is as wide as whichever pane it has open. Measured and observed, so
  // opening or closing a pane re-flows the card.
  const chromeInsetLeft = useChromeInsetLeft();
  const maximizedLeft = Math.max(
    (chromeInsetLeft ?? 0) + MAXIMIZED_GAP,
    MAXIMIZED_LEFT_FALLBACK
  );

  // The three ways out of the dock. They differ only in the size the card comes
  // back at, which is the store's one job here.
  const restore = useCallback(() => undock(), [undock]);
  const restoreMaximized = useCallback(() => undock(true), [undock]);
  const liftFromDock = useCallback(() => undock(false), [undock]);

  const panelRef = useRef<HTMLDivElement>(null);

  // Opening the card moves focus into it, so the keyboard follows the eye and
  // a screen reader announces the dialog. Once only, on the way in: no trap,
  // and nothing takes focus back when it closes, because the card is opened
  // from a map popup that is gone by then.
  useEffect(() => {
    panelRef.current?.focus();
  }, []);
  // A maximized card owns its geometry, so its bar is not a handle - but a
  // DOCKED one is a bar whatever the card behind it was, and dragging it up is
  // how the card comes back. So the drag is off only for a maximized card that
  // is on screen. Lifting a maximized card out of the dock un-maximizes it:
  // the drag is about to place the card, and a card that fills the pane has
  // nowhere to be placed. `draggable` is what the card's own cursor and
  // geometry answer to, and stays false while docked - the bar is a click
  // first.
  const draggable = !maximized && !minimized;
  const drag = usePanelDrag({
    panelRef,
    enabled: minimized || !maximized,
    width: panelWidth,
    leftBound: maximizedLeft,
    startsOnLift: minimized,
    onLift: liftFromDock,
    onCommit: setPosition
  });

  // A handle can move the card as well as size it, so both are written in one
  // go: the left one walks the corner leftwards as the card grows, and the
  // right one plants a card the CSS was holding against the window the first
  // time it changes its width. A width on its own leaves the placement alone.
  const commitResize = useCallback(
    (width: number, movedTo: Maybe<PanelPosition>) => {
      setPanelWidth(width);
      if (movedTo) setPosition(movedTo);
    },
    [setPanelWidth, setPosition]
  );

  // Handles on both side edges and along the bottom, off for the two states
  // that own their own geometry: a maximized card is sized by the window, and
  // a docked one is a bar whose size is not the user's to set.
  const resize = usePanelResize({
    panelRef,
    width: panelWidth,
    height: panelHeight,
    position,
    leftBound: maximizedLeft,
    enabled: draggable,
    onCommit: commitResize,
    onCommitHeight: setPanelHeight
  });

  // A stored position is clamped on read, but the window can be resized after
  // that - and while the card is closed, nothing is watching. Re-clamping when
  // it opens, and whenever the window changes size, is what keeps a card that
  // was left near an edge from opening off screen.
  //
  // The width is one of the things that moves the card's far edge, so widening
  // a card that is already near the right of the window walks it back left.
  // The width used is the one the card is drawn at rather than the one it is
  // stored at, and it is read inside `settle` so that a resize is measured
  // against the window as it now is.
  useLayoutEffect(() => {
    if (!position || !draggable) return;
    const settle = () => {
      const clamped = clampPanelPosition(
        position,
        effectivePanelWidth(panelWidth, maximizedLeft)
      );
      if (clamped.x !== position.x || clamped.y !== position.y) {
        setPosition(clamped);
      }
    };
    settle();
    window.addEventListener("resize", settle);
    return () => window.removeEventListener("resize", settle);
  }, [position, panelWidth, maximizedLeft, draggable, setPosition]);

  const selectedWell = useSelectedWellFromStore();
  const selectedUwi = selectedWell?.Uwi;

  // Nothing pinned yet, so the first selection seeds the card. After that the
  // card holds its well until an explicit "show me this one" gesture.
  useEffect(() => {
    if (!targetUwi && selectedUwi) {
      setTargetUwi(selectedUwi);
    }
  }, [targetUwi, selectedUwi, setTargetUwi]);

  const {
    data: wellData,
    isLoading: fieldsLoading,
    isError: fieldsFailed,
    isPreviousData: fieldsAreStale,
    refetch: refetchFields
  } = useWellCardPanelFields(targetUwi);

  const needsProduction = activeTab === "Summary" || activeTab === "Production";

  // The first tab that shows the chart mounts it, and it stays mounted for as
  // long as the card is on that well: Details and Gunbarrel hide it rather than
  // take it down. Its settings provider holds every change the user made to it,
  // so a remount would put the chart back to the card's seed and refetch - see
  // the note in `PanelChart`. A new target closes the gate again, so a well with
  // no production never mounts one, and nothing is fetched before a tab asks.
  const chartMountedForRef = useRef<Maybe<string>>(null);
  useEffect(() => {
    if (needsProduction) chartMountedForRef.current = targetUwi;
  }, [needsProduction, targetUwi]);
  const productionRequested =
    needsProduction || (!!targetUwi && chartMountedForRef.current === targetUwi);

  const production = useProductionArrayData(targetUwi, productionRequested);

  // A user-drawn line owns the section while it stands, so the derive stands
  // down without touching it. Closing the card unmounts this component, which
  // is where a line the derive owns gets cleared.
  const line = useDeriveXdaLineForWell(targetUwi, halfLengthMeters, customLineActive);

  const hasWell = !!targetUwi;
  // Only the field fetch gates the header, the tab strip and the blocks. The
  // production fetch starts on the first switch to a tab that needs it, and
  // letting it reach this flag blanked the well's name on every such switch.
  const fieldsPending = hasWell && fieldsLoading;
  const showBlocks = hasWell && !fieldsFailed && !fieldsPending;
  const showChart = showBlocks && needsProduction;
  const productionPending = production.isLoading;
  const productionFailed = production.isError;
  const hasProduction =
    !productionPending && !productionFailed && production.hasProduction;
  // Shown on the two tabs that need it, kept mounted on the other two.
  const chartMounted = showBlocks && productionRequested && hasProduction;
  const showNoProduction =
    showChart && !productionPending && !productionFailed && !production.hasProduction;
  const scrolls = activeTab !== "Gunbarrel" && showBlocks;
  // keepPreviousData holds the last well's values under the new well's name
  // until the response lands. Dimmed, not hidden: blanking is what
  // keepPreviousData exists to avoid.
  const isStale =
    hasWell &&
    !fieldsPending &&
    (fieldsAreStale || (needsProduction && production.isPreviousData));

  const formattedUwi = targetUwi ? new Uwi().toFormatted(targetUwi) : null;

  // Escape no longer takes the section line down while the card is open:
  // `XdaLineMarker` gates that binding on the panel the same way it gates its
  // derive, so the line the card owns is the card's to clear. Escape still
  // clears the map's selection and highlight, the card's own included, but
  // leaves the card open.

  const label = formattedUwi ? `Well card for ${formattedUwi}` : "Well card";
  const titleBar = (
    <PanelTitleBar
      draggable={draggable}
      dragging={drag.dragging}
      onPointerDown={drag.onPointerDown}
      minimized={minimized}
      maximized={maximized}
      formattedUwi={formattedUwi}
      onMinimize={() => setMinimized(true)}
      onRestore={restore}
      onRestoreMaximized={restoreMaximized}
      onToggleMaximized={toggleMaximized}
      onClose={() => setOpen(false)}
    />
  );

  /*
   * The same sheet the dashboard pane puts up for a widget going fullscreen,
   * in the same place and under the same class, so the screenshot machinery
   * finds the embed exactly where it looks for a widget's:
   * `getChartPlotSelector` prefers `.fullscreen .ChartPlot`, and the viewers
   * are found by a selector that would otherwise match whatever is first in
   * the document.
   *
   * The card is hidden rather than covered while the sheet is up. It is fixed
   * at z-index 50 and the sheet is a plain positioned sibling, so covering it
   * would need the two to agree about stacking; hiding is one property, and it
   * leaves every box inside the card measurable, which a display:none card
   * would not.
   */
  const screenshotSheet = screenshotNode ? (
    <div className="fullscreen" data-testid="well-card-screenshot-sheet">
      <OutPortal node={screenshotNode} />
    </div>
  ) : null;

  /*
   * Both returns are the same fragment holding the same two children, and that
   * is load-bearing rather than tidy. React reconciles on element type: a
   * docked card that returned a bare Panel and an open one that returned a
   * fragment are different types, so restoring the card would unmount the
   * Panel and build a new one - taking the title bar's DOM node, and with it
   * the pointer capture, in the middle of the drag that is doing the
   * restoring. Dragging the card out of the dock then died on the first move.
   */

  // Docked, the card is the title bar alone. Everything below it is left
  // unmounted rather than clipped to nothing: the gunbarrel's viewers measure
  // their container, and one mounted at no height never recovers.
  if (minimized) {
    return (
      <>
        {screenshotSheet}
        <Panel
          ref={panelRef}
          $maximized={maximized}
          $maximizedLeft={maximizedLeft}
          $minimized
          $position={null}
          $width={panelWidth}
          $height={null}
          $dragging={false}
          role="dialog"
          aria-label={label}
          className="well-card-panel"
          data-testid="well-card-panel">
          {titleBar}
        </Panel>
      </>
    );
  }

  return (
    <>
      {screenshotSheet}
      <Panel
        ref={panelRef}
        $maximized={maximized}
        $maximizedLeft={maximizedLeft}
        $minimized={false}
        $position={drag.livePosition ?? resize.livePosition ?? position}
        $width={resize.liveWidth ?? panelWidth}
        $height={resize.liveHeight ?? panelHeight}
        $dragging={drag.dragging || resize.resizing}
        $hidden={!!screenshotNode}
        role="dialog"
        aria-label={label}
        /* A stable hook for the card's own descendants: the chart listens here
           for the end of the geometry animation. */
        className="well-card-panel"
        // Focusable so the card can be given focus when it opens, but not a
        // tab stop of its own once the user is past it.
        tabIndex={-1}
        data-testid="well-card-panel">
        {/* Either side edge sets the width and the bottom one sets the height,
            and each one moves the edge it is on. There is no top handle: the
            card's top edge is where the card has been dragged to, and the
            height already follows it.
            Drawn before the rest of the card so a strip is never buried under
            a block that reaches the edge, and left out entirely for a
            maximized card - the window sizes that one, so a resize cursor on
            its edge would be offering something it cannot do. Named for a
            screen reader, but pointer-only: no keyboard path, the same as the
            activity bar's `ResizeHorizontal`. */}
        {draggable && (
          <>
            <ResizeHandle
              $side="left"
              onPointerDown={resize.onPointerDown("left")}
              role="separator"
              aria-orientation="vertical"
              aria-label="Resize well card (left edge)"
              data-testid="well-card-panel-resize-left"
            />
            <ResizeHandle
              $side="right"
              onPointerDown={resize.onPointerDown("right")}
              role="separator"
              aria-orientation="vertical"
              aria-label="Resize well card (right edge)"
              data-testid="well-card-panel-resize-right"
            />
            <ResizeHandle
              $side="bottom"
              onPointerDown={resize.onPointerDown("bottom")}
              role="separator"
              aria-orientation="horizontal"
              aria-label="Resize well card (bottom edge)"
              data-testid="well-card-panel-resize-bottom"
            />
          </>
        )}

        {titleBar}

        {showBlocks && (
          <WellCardPanelHeader
            formattedUwi={formattedUwi}
            rawUwi={targetUwi}
            wellData={wellData}
            stale={isStale}
          />
        )}
        {fieldsPending && <HeaderSkeleton />}

        <TabStrip $dim={!hasWell}>
          {/* The link sits outside the tablist so it is not announced as a tab. */}
          <TabList role="tablist" aria-label="Well card sections">
            {WELL_CARD_PANEL_TABS.map((tab) => (
              <Tab
                key={tab}
                id={TAB_ID(tab)}
                type="button"
                role="tab"
                aria-selected={tab === activeTab}
                aria-controls={TAB_PANEL_ID}
                $active={tab === activeTab && hasWell}
                onClick={() => setActiveTab(tab)}
                data-testid={`well-card-panel-tab-${tab.toLowerCase()}`}>
                {tab}
              </Tab>
            ))}
          </TabList>
          <StateFilingsLink uwi={targetUwi} wellData={wellData} stale={fieldsAreStale} />
        </TabStrip>

        <PanelBody
          id={TAB_PANEL_ID}
          role="tabpanel"
          aria-labelledby={TAB_ID(activeTab)}
          $scrolls={scrolls}
          $stale={isStale}>
          {!hasWell && <NoSelectionState />}
          {fieldsPending && <BodySkeleton />}

          {hasWell && !fieldsPending && fieldsFailed && (
            <PanelErrorState
              title="Could not load this well's data"
              detail="The request for this well's fields failed."
              onRetry={refetchFields}
              testId="well-card-panel-fields-error"
            />
          )}

          {showChart && productionPending && <ChartSkeleton />}
          {showChart && productionFailed && (
            <PanelErrorState
              compact
              title="Could not load production"
              detail={
                production.filterUnavailable
                  ? "The dashboard's filter has not finished loading, so production could not be requested for this well."
                  : "The production request failed. This is not the same as a well with no reported production."
              }
              onRetry={production.refetch}
              testId="well-card-panel-production-error"
            />
          )}
          {chartMounted && (
            <ChartMount
              hidden={!needsProduction}
              data-testid="well-card-panel-chart-mount">
              <PanelChart uwi={targetUwi} />
            </ChartMount>
          )}
          {showNoProduction && <NoProductionState />}

          {showBlocks && activeTab === "Summary" && (
            <SummaryTab wellData={wellData} hasProduction={hasProduction} />
          )}
          {showBlocks && activeTab === "Production" && (
            <ProductionTab
              wellData={wellData}
              rows={production.rows}
              units={production.units}
              hasProduction={hasProduction}
              rowsFailed={production.rowsFailed}
              notNormalizable={production.notNormalizable}
              normalizedBy={production.normalizedBy}
              onRetry={production.refetch}
            />
          )}
          {showBlocks && activeTab === "Gunbarrel" && (
            <GunbarrelTab wellData={wellData} line={line} />
          )}
          {showBlocks && activeTab === "Details" && <DetailsTab uwi={targetUwi} />}
        </PanelBody>
      </Panel>
    </>
  );
}

interface PanelTitleBarProps {
  draggable: boolean;
  dragging: boolean;
  onPointerDown: (event: React.PointerEvent) => void;
  minimized: boolean;
  maximized: boolean;
  formattedUwi: Maybe<string>;
  onMinimize: () => void;
  onRestore: () => void;
  onRestoreMaximized: () => void;
  onToggleMaximized: () => void;
  onClose: () => void;
}

/**
 * The same bar in both states, so the card cannot dock into something that
 * looks like a different control. Docked, the whole strip restores the card:
 * it runs the width of the screen, and hunting for one icon on a taskbar is
 * not what a user does. Each button stops the click from reaching it.
 */
function PanelTitleBar({
  draggable,
  dragging,
  onPointerDown,
  minimized,
  maximized,
  formattedUwi,
  onMinimize,
  onRestore,
  onRestoreMaximized,
  onToggleMaximized,
  onClose
}: PanelTitleBarProps): JSX.Element {
  const stop = (handler: () => void) => (event: React.MouseEvent) => {
    event.stopPropagation();
    handler();
  };

  /*
   * Docked, the whole strip restores the card, so it says so on hover the
   * way the rest of the app's controls do. Open, it is a drag handle whose
   * buttons carry their own tooltips, and a hint following the pointer
   * across it would be noise.
   */
  return (
    <Tooltip title={minimized ? "Restore" : ""}>
      <TitleBar
        $docked={minimized}
        $draggable={draggable}
        $dragging={dragging}
        onPointerDown={onPointerDown}
        onClick={minimized ? onRestore : undefined}
        /* Docked, the whole strip is the control that brings the card back, so
           it answers a keyboard as well as a pointer. Undocked it is a drag
           handle with its own buttons, and neither a role nor a tab stop. */
        role={minimized ? "button" : undefined}
        tabIndex={minimized ? 0 : undefined}
        onKeyDown={
          minimized
            ? (event) => {
                if (event.key !== "Enter" && event.key !== " ") return;
                // Space scrolls the page otherwise.
                event.preventDefault();
                onRestore();
              }
            : undefined
        }
        aria-label={minimized ? "Restore the well card" : undefined}
        data-testid="well-card-panel-title-bar">
        <Title>
          Well Card
          {/* Docked, the bar is all there is to say which well is behind it. */}
          {minimized && formattedUwi && <DockedUwi>{formattedUwi}</DockedUwi>}
        </Title>
        {minimized ? (
          /* The same three controls as the open card, in the same order and on
             the same side, so the bar is the card set aside rather than a
             different widget. Only the first one changes its job: docked, it
             brings the card back instead of putting it away.

             Fullscreen keeps the "go fullscreen" icon here whatever the card
             behind the bar was, because from the dock that is the only thing it
             does - a card docked while maximized comes back maximized either
             way, and an exit-fullscreen icon that restores a maximized card
             would be pointing the wrong way. */
          <>
            <Tooltip title="Restore">
              <TitleButton
                type="button"
                aria-label="Restore"
                onClick={stop(onRestore)}
                data-testid="well-card-panel-restore">
                <IconComponent path={mdiWindowRestore} size={ICON_SIZE} />
              </TitleButton>
            </Tooltip>
            <Tooltip title="Maximize">
              <TitleButton
                type="button"
                aria-label="Maximize"
                onClick={stop(onRestoreMaximized)}
                data-testid="well-card-panel-maximize">
                <IconComponent path={mdiFullscreen} size={ICON_SIZE_FULLSCREEN} />
              </TitleButton>
            </Tooltip>
          </>
        ) : (
          <>
            <Tooltip title="Minimize">
              <TitleButton
                type="button"
                aria-label="Minimize"
                onClick={stop(onMinimize)}
                data-testid="well-card-panel-minimize">
                <MinimizeLine aria-hidden="true" />
              </TitleButton>
            </Tooltip>
            <Tooltip title={maximized ? "Restore" : "Maximize"}>
              <TitleButton
                type="button"
                aria-label={maximized ? "Restore" : "Maximize"}
                onClick={stop(onToggleMaximized)}
                data-testid="well-card-panel-maximize">
                <IconComponent
                  path={maximized ? mdiFullscreenExit : mdiFullscreen}
                  size={ICON_SIZE_FULLSCREEN}
                />
              </TitleButton>
            </Tooltip>
          </>
        )}
        <Tooltip title="Close">
          <TitleButton
            type="button"
            aria-label="Close"
            onClick={stop(onClose)}
            data-testid="well-card-panel-close">
            <IconComponent path={mdiClose} size={ICON_SIZE} />
          </TitleButton>
        </Tooltip>
      </TitleBar>
    </Tooltip>
  );
}

// Fixed, not absolute: the pane it sits in scrolls and is not a positioning
// context, and the design's offsets are measured from the window.
//
// z-index 50 puts the card over the map and the widgets it floats above, and
// UNDER the application's own chrome, which is the rule: the card is dashboard
// content. The navigation bar wins by construction - `HomeGrid` carries
// z-index 0 and so opens a stacking context, and the bar is its sibling at
// z-index 1, so nothing inside the dashboard can rise above it whatever value
// it takes. The activity bar is inside that same context and had no value of
// its own, which is why the card painted over it; it now carries 60.
//
// Minimized, it becomes a bar across the foot of the screen. The geometry is
// animated rather than the card being swapped out, so the card slides down to
// the bar and back up out of it.
/**
 * Where a maximized card starts when there is no rail to measure, and the
 * floor it never drops below: the collapsed rail is sixty pixels wide, and
 * this is the design's offset from it.
 */
/**
 * The tab strip and the one body it drives. One body, not four: the card
 * renders the active tab's blocks into the same region, so every tab points at
 * it and it names whichever tab is selected.
 */
const TAB_ID = (tab: string) => `well-card-panel-tab-${tab.toLowerCase()}`;
const TAB_PANEL_ID = "well-card-panel-tabpanel";

const MAXIMIZED_LEFT_FALLBACK = 84;
/**
 * The gutter a maximized card keeps on its other three edges, so its left edge
 * stands off the rail by the same amount rather than sitting flush against it.
 * With the rail collapsed the fallback above is the wider of the two and wins,
 * which is what keeps the untouched case exactly as it was.
 */
const MAXIMIZED_GAP = 14;

const Panel = styled.div<{
  $maximized: boolean;
  $maximizedLeft: number;
  $minimized: boolean;
  $position: Maybe<PanelPosition>;
  $width: number;
  $height: Maybe<number>;
  $dragging: boolean;
  $hidden?: boolean;
}>`
  position: fixed;
  top: ${(props) =>
    props.$minimized ? "calc(100vh - " + DOCKED_HEIGHT + "px)" : "58px"};
  right: ${(props) => (props.$minimized ? "0" : "14px")};
  bottom: ${(props) => (props.$minimized ? "0" : "14px")};
  /* Docked, the bar sits in the bottom-right corner at the card's default
     width: it is one card set aside, not a taskbar for the whole screen, and
     not the user's resized width either - the bar is a bar whatever size the
     card behind it was left at.
     Maximized, it starts at the rail's measured right edge rather than at a
     fixed offset that assumed the rail was collapsed - with a pane open the
     card began underneath it, and the rail sits above the card by the chrome
     rule, so the pane simply covered it. */
  left: ${(props) =>
    props.$minimized
      ? "auto"
      : props.$maximized
      ? props.$maximizedLeft + "px"
      : /* Its own width from the right, until the window is too narrow for
           that: then it starts where the chrome ends instead of hanging off
           the left edge, and takes whatever width is left. Derived from the
           width rather than written out, so the two cannot drift. */
        "max(" +
        props.$maximizedLeft +
        "px, calc(100% - " +
        (props.$width + PANEL_GUTTER) +
        "px))"};
  ${(props) =>
    props.$minimized && "width: min(" + PANEL_WIDTH + "px, calc(100vw - 14px));"}
  /* Whatever the window does, the card keeps a usable height: past this it
     hangs over the bottom edge rather than collapsing to nothing, so its
     title bar and controls stay in reach. Not while docked - that IS a bar,
     and a floor meant for the card would push it off the bottom. */
  ${(props) => !props.$minimized && "min-height: " + PANEL_MIN_HEIGHT + "px;"}
  z-index: 50;
  background: var(--white);
  border: 1px solid var(--cloud);
  /* Docked, only the top corners round: the bottom two meet the edge of the
     screen, where a radius would leave two slivers of map showing through. */
  border-radius: ${(props) => (props.$minimized ? "8px 8px 0 0" : "8px")};
  box-shadow: 0 14px 40px rgba(9, 30, 66, 0.28), 0 2px 8px rgba(9, 30, 66, 0.12);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: top 220ms cubic-bezier(0.25, 0.5, 0.25, 1),
    left 220ms cubic-bezier(0.25, 0.5, 0.25, 1),
    right 220ms cubic-bezier(0.25, 0.5, 0.25, 1),
    bottom 220ms cubic-bezier(0.25, 0.5, 0.25, 1);

  /* Dragged: the card takes its position from the corner the user left it at,
     and is bounded by the window from there - it runs to the bottom gutter and
     no further, rather than keeping a fixed height that a low position or a
     short window would push off the screen. It shrinks as it goes down, to the
     floor above. Written after the insets so it wins. */
  ${(props) =>
    props.$position &&
    !props.$maximized &&
    !props.$minimized &&
    `
    left: ${props.$position.x}px;
    top: ${props.$position.y}px;
    right: auto;
    bottom: auto;
    /* The width is the card's own, capped only by the window - never by how
       far right it has been dragged. Taking the drag's x into account here let
       the right-hand edge of the screen squeeze the card as it was moved
       towards it, and a squeezed card put a scrollbar under its own tables. */
    width: min(
      ${props.$width}px,
      calc(100vw - ${props.$maximizedLeft}px - ${PANEL_GUTTER}px)
    );
    height: calc(100vh - ${props.$position.y}px - ${PANEL_GUTTER}px);
  `}

  /* Dragged by the bottom edge: the card keeps the height it was given instead
     of running to the foot of the window, wherever it then sits. Written after
     both of the blocks above so it wins over the derived height, and over the
     insets that size an undragged card. The auto bottom goes with it, because
     a top, a bottom and a height together are one constraint too many, and the
     browser answers that by dropping one of them itself.
     Capped by what is left below the card's top edge, the way the width is
     capped by what is left beside it: the window can be made shorter after the
     card was sized, and nothing watches it while the card is closed. The floor
     above still wins against the cap, so a window shorter than the card leaves
     it hanging over the bottom rather than collapsed.
     A card nobody has dragged this edge on has no height at all here, and goes
     on filling to the bottom gutter exactly as it did.
     NOTE: no backticks in this comment - one inside a styled-components
     template ends the literal, and the file stops parsing from here on. */
  ${(props) => {
    if (!props.$height || props.$maximized || props.$minimized) return "";
    const roomBelowTop = props.$position
      ? `calc(100vh - ${props.$position.y}px - ${PANEL_GUTTER}px)`
      : `calc(100vh - ${PANEL_VERTICAL_MARGIN}px)`;
    return `
    height: min(${props.$height}px, ${roomBelowTop});
    bottom: auto;
  `;
  }}

  /* Under the cursor there is nothing to ease: the card has to track it. */
  ${(props) => props.$dragging && "transition: none;"}

  /* One of the card's embeds is in the fullscreen sheet. Hidden, not removed:
     the card keeps its position, its tab and its scroll, every box inside it
     stays measurable, and coming back is one property. Written last so it
     wins, and with the transition off so the card does not fade. */
  ${(props) =>
    props.$hidden &&
    `
    visibility: hidden;
    transition: none;
  `}
`;

/*
 * A grab strip down each side edge of the card, and one along its foot. It
 * paints nothing at all, in any state: the design is a plain card, and a rule
 * down its side reads as a border it does not have whether it is there always
 * or only on hover. The cursor is what says the edge can be dragged, which is
 * how the rest of the application's resizable edges announce themselves, and
 * it names the direction that edge moves in.
 *
 * Six pixels across and the full length of the edge it is on, which is the
 * same strip `ResizeHorizontal` gives the activity bar. It lies over the
 * card's own content rather than taking a column or a row of it, so nothing
 * inside the card has to make room; `touch-action` keeps a touch drag from
 * scrolling instead.
 *
 * Hung half over the edge rather than held flush inside it: the body scrolls,
 * and a flush strip covered the outer six pixels of its scrollbar. The card
 * clips what overflows it, so the outer half is not grabbable and what is left
 * is a three pixel strip on the card's own edge - which is the trade this
 * makes, half the scrollbar back for half the grab.
 *
 * The side strips run the whole height, so the two bottom corners are shared
 * with this one. It is drawn last and they carry the same z-index, so the
 * corners answer to the bottom edge - which is the one whose limit the user is
 * up against when he is aiming at a corner at all.
 */
const ResizeHandle = styled.div<{ $side: "left" | "right" | "bottom" }>`
  position: absolute;
  z-index: 1;
  touch-action: none;
  ${(props) =>
    props.$side === "bottom"
      ? `
    left: 0;
    right: 0;
    bottom: -3px;
    height: 6px;
    cursor: ns-resize;
  `
      : `
    top: 0;
    bottom: 0;
    ${props.$side === "left" ? "left: -3px;" : "right: -3px;"}
    width: 6px;
    cursor: ew-resize;
  `}
`;

/**
 * `@mdi/react` sizes an icon as `1.5 x size` rem, and this app's root is 62.5%
 * (1rem = 10px), so one unit is 15px. One nominal size for all three controls,
 * at just under 20px, which is what they needed to stop reading as
 * afterthoughts.
 *
 * The fullscreen pair is the exception, and it is an OPTICAL one rather than a
 * nominal one: `mdiFullscreen` is four corner brackets with a hole in the
 * middle, so at the same nominal size it reads smaller than the X beside it.
 * One step up evens the two out.
 *
 * Maximize uses that fullscreen pair, the same one the vis toolbars use
 * (`VisFullscreenToggle`), rather than arrows of its own.
 */
const ICON_SIZE = 1.33;
const ICON_SIZE_FULLSCREEN = 1.4;

/** The docked bar's height, and so the card's height while docked. */
const DOCKED_HEIGHT = 36;

const TitleBar = styled.div<{
  $docked: boolean;
  $draggable: boolean;
  $dragging: boolean;
}>`
  display: flex;
  align-items: center;
  background: var(--color-text);
  color: #b5b5b5;
  padding: 4px 6px 4px 12px;
  flex: none;
  /* The bar is the card's handle, so it says so. */
  cursor: ${(props) =>
    props.$dragging ? "grabbing" : props.$draggable ? "grab" : "default"};
  /* A drag would otherwise select the title's text as it goes. */
  user-select: none;
  touch-action: none;
  /* Docked, the bar fills the card rather than asking for its own height: the
     card is DOCKED_HEIGHT tall including its border, so a bar of exactly that
     height overflows the content box by the border and has its last two
     pixels clipped. */
  ${(props) => props.$docked && "height: 100%; cursor: pointer;"}
`;

// The well the docked bar belongs to, beside the card's name.
const DockedUwi = styled.span`
  margin-left: 10px;
  font-family: var(--fontMono);
  font-weight: 400;
  color: var(--lead);
`;

const Title = styled.span`
  flex: 1;
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0.02em;
`;

/*
 * Minimize is drawn here rather than taken from mdi. Every dash in the set is
 * either heavier than `mdiClose`'s 2px strokes (`mdiWindowMinimize` is a 4px
 * bar) or a different length, and the three controls sit side by side where
 * any mismatch shows. A 12 x 1.5px rule in `currentColor` carries the button's
 * colour and its hover with it.
 */
const MinimizeLine = styled.span`
  width: 12px;
  height: 1.5px;
  background: currentColor;
  flex: none;
`;

// The dashboard widget header's button, in the panel's title bar: transparent,
// icon-grey, teal on hover. The box is sized around the icon rather than the
// other way round — a 20px icon in a 24px-tall button leaves nothing to aim at.
const TitleButton = styled.button`
  border: 0;
  background: transparent;
  color: var(--color-icon);
  cursor: pointer;
  width: 32px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: color var(--duration);

  &:hover {
    color: ${TEAL};
  }
`;

// The type well editor's tab headers (`TypeWellEditorWidget.tsx`), which are
// antd `Tabs` themed there rather than a shared component: a plain row over a
// hairline, grey labels, the active one in ink and bold, and a 3px teal bar
// under it. Copied rather than imported because switching this strip to antd
// `Tabs` would mean handing it the panes as children, and the body renders the
// active tab itself.
const TabStrip = styled.div<{ $dim: boolean }>`
  display: flex;
  align-items: stretch;
  background: var(--white);
  border-bottom: 1px solid var(--color-shadow);
  flex: none;
  opacity: ${(props) => (props.$dim ? 0.5 : 1)};
`;

const Tab = styled.button<{ $active: boolean }>`
  height: 38px;
  padding: 0 15px;
  border: none;
  background: transparent;
  cursor: pointer;
  font-family: inherit;
  font-size: 1.4rem;
  letter-spacing: 0.5px;
  color: ${(props) => (props.$active ? "var(--color-text)" : "var(--color-icon)")};
  font-weight: ${(props) => (props.$active ? 700 : 400)};
  /* antd draws its ink bar as an element of its own; an inset shadow puts the
     same 3px bar on the tab without adding one, and it cannot drift out of
     step with the label it belongs to. */
  box-shadow: ${(props) =>
    props.$active ? "inset 0 -3px 0 0 var(--color-primary)" : "none"};

  &:hover {
    color: var(--color-text);
  }
`;

const TabList = styled.div`
  display: flex;
`;

const PanelBody = styled.div<{ $scrolls: boolean; $stale: boolean }>`
  flex: 1;
  min-height: 0;
  background: var(--white);
  display: flex;
  flex-direction: column;
  overflow-y: ${(props) => (props.$scrolls ? "auto" : "hidden")};
  /* The card is as wide as its widest table needs; anything that still does
     not fit is clipped rather than put behind a sideways scrollbar. */
  overflow-x: hidden;
  opacity: ${(props) => (props.$stale ? 0.4 : 1)};
  transition: opacity 120ms linear;
`;

/* The chart's own place in the body, held across every tab. `hidden` rather
   than a removal, and the rule is declared here rather than left to the
   browser's own sheet, so nothing this component sets later can win against
   it. `useChartSettleResize` looks for this attribute and stands down while it
   is set: a hidden box measures zero, and the chart must not be resized to
   that. */
const ChartMount = styled.div`
  flex: none;

  &[hidden] {
    display: none;
  }
`;
